import React, { useCallback, useEffect, useState } from 'react';
import { useAssistantWidget } from '../../contexts/assistant-widget-context';
import { usePeersona } from '../../contexts/peersona-context';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { useLockBody } from '../../hooks/utils/useLockBody';
import { AssistantPanelHeader } from '../assistant-widget/assistant-panel-header';
import { ConversationsListView } from '../assistant-widget/conversations-list-view';
import { NewConversationListView } from '../assistant-widget/new-conversation-list-view';
import { PeersonaHeader } from './peersona-header';
import { PeersonaMessageArea } from './peersona-message-area';
import { PeersonaInputBar } from './peersona-input-bar';
import { PeersonaAboutDrawer } from './peersona-about-drawer';
import { PEERSONA_PANEL_BG } from './peersona-styles';

/**
 * Floating Peersona chat panel. Hosts four in-place layouts: chat,
 * Conversations list, New-conversation list, and the About drawer
 * (overlays chat to preserve thread state).
 */
export const PeersonaPanel: React.FC = () => {
  const { panelOpen, closePanel } = usePeersona();
  const { assistantPanelView } = useAssistantWidget();
  const isMdUp = useMediaQuery('md');
  useLockBody(panelOpen && !isMdUp);
  const [aboutOpen, setAboutOpen] = useState(false);

  const openAbout = useCallback(() => setAboutOpen(true), []);
  const closeAbout = useCallback(() => setAboutOpen(false), []);

  // Reset to chat layout for next open; re-opens almost always go straight to chat.
  useEffect(() => {
    if (!panelOpen && aboutOpen) setAboutOpen(false);
  }, [panelOpen, aboutOpen]);

  const isListView = assistantPanelView === 'conversations' || assistantPanelView === 'new';
  const listTitle = assistantPanelView === 'conversations' ? 'Conversations' : 'Start a New Conversation';
  const messageAreaProps = { onLearnMore: openAbout };

  return (
    <div
      className={[
        'fixed flex flex-col bg-white',
        panelOpen ? '' : 'hidden',
        isMdUp
          ? // 550px wide, anchored to the bottom-right (the Fi bubble hides while the panel is open).
            'bottom-5 right-5 top-[120px] z-[50] w-full max-w-[550px] overflow-hidden rounded-[24px] shadow-[0px_4px_20px_rgba(0,0,0,0.37)]'
          : 'inset-0 z-[70] max-w-none',
      ].join(' ')}
      role="dialog"
      aria-label="Peersona chat"
      aria-hidden={!panelOpen}
    >
      {aboutOpen ? (
        <PeersonaAboutDrawer onBack={closeAbout} onClose={closePanel} />
      ) : isListView ? (
        <div className="flex min-h-0 flex-1 flex-col bg-white">
          <div
            className={
              isMdUp
                ? 'bg-white px-2.5 pt-3'
                : 'sticky top-0 z-[2] bg-white px-2.5 pt-[calc(env(safe-area-inset-top,0px)+12px)]'
            }
          >
            <AssistantPanelHeader mode="list" listTitle={listTitle} onClose={closePanel} />
          </div>
          {assistantPanelView === 'conversations' ? <ConversationsListView /> : <NewConversationListView />}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={
              isMdUp
                ? 'bg-white px-2.5 pt-3'
                : 'sticky top-0 z-[2] bg-white px-2.5 pt-[calc(env(safe-area-inset-top,0px)+12px)]'
            }
          >
            <PeersonaHeader onOpenAbout={openAbout} />
          </div>

          {isMdUp ? (
            <div className="flex min-h-0 flex-1 flex-col gap-2 pb-5" style={{ backgroundColor: PEERSONA_PANEL_BG }}>
              <PeersonaMessageArea {...messageAreaProps} />
              <div className="px-2.5">
                <PeersonaInputBar />
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col" style={{ backgroundColor: PEERSONA_PANEL_BG }}>
              <PeersonaMessageArea
                {...messageAreaProps}
                scrollable
                className="pb-[calc(18px+env(safe-area-inset-bottom,0px))]"
              />
              <div className="sticky bottom-0 z-[1] px-2.5 pb-[calc(10px+env(safe-area-inset-bottom,0px))]">
                <PeersonaInputBar />
              </div>
              <button type="button" className="sr-only" aria-label="Close Peersona" onClick={closePanel} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
